import { fetchJson } from "@/services/api-client";
import { Member } from "@/types/member";
import {
  Organization,
  OrganizationMember,
  OrganizationMemberInvite,
  OrganizationMemberInviteDetails,
  OrganizationMemberInviteUpdateData,
  OrganizationMemberUpdateData,
} from "@/types/organization";
import { User } from "@/types/user";

export type OrganizationJoinRequestStatus = {
  request: {
    id: string;
    status: "active" | "invited" | "declined" | "requested" | "rejected";
    organizationId: string;
  } | null;
  organization: Organization | null;
};

export async function getOrganizationJoinRequestStatus(
  organizationId: string,
): Promise<OrganizationJoinRequestStatus> {
  return fetchJson<OrganizationJoinRequestStatus>(
    `/api/member/request?orgId=${encodeURIComponent(organizationId)}&_=${Date.now()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to get organization join request" },
  );
}

export async function getPendingOrganizationJoinRequest(): Promise<OrganizationJoinRequestStatus> {
  return fetchJson<OrganizationJoinRequestStatus>(
    `/api/member/request?_=${Date.now()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to get organization join request" },
  );
}

export async function createOrganizationMember(
  userId: string,
  organizationId: string,
  inviterId: string,
  inviteToken: string,
  role: string,
  status: string,
): Promise<Member> {
  const { member } = await fetchJson<{ member: Member }>(
    "/api/member",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        organizationId,
        inviterId,
        inviteToken,
        role,
        status,
      }),
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to create organization user" },
  );

  return member;
}

export async function deleteOrganizationMember(
  memberId: string,
  userId: string,
  organizationId: string,
): Promise<void> {
  await fetchJson<unknown>(
    "/api/member",
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({ memberId, userId, organizationId }),
    },
    { fallbackErrorMessage: "Failed to delete organization user" },
  );
}

export async function getOrganizationMembers(
  organizationId: string,
  forceRefresh = false,
): Promise<Member[]> {
  const url = forceRefresh
    ? `/api/member?orgId=${organizationId}&_=${Date.now()}`
    : `/api/member?orgId=${organizationId}`;

  const { members } = await fetchJson<{ members: Member[] }>(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to get organization members" },
  );

  return members;
}

export async function getOrganizationMember(
  userId: string,
  organizationId: string,
  forceRefresh = false,
): Promise<OrganizationMember> {
  const url = forceRefresh
    ? `/api/member?orgId=${organizationId}&userId=${userId}&_=${Date.now()}`
    : `/api/member?orgId=${organizationId}&userId=${userId}`;

  const { organizationMember } = await fetchJson<{
    organizationMember: OrganizationMember;
  }>(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to get organization user" },
  );

  return organizationMember;
}

export async function sendOrganizationInvites(
  organization: Organization,
  user: User,
  invites: OrganizationMemberInvite[],
): Promise<OrganizationMemberInvite[]> {
  const { results } = await fetchJson<{ results: OrganizationMemberInvite[] }>(
    "/api/invite",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization, user, invites }),
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to send organization user invites" },
  );

  return results;
}

export async function updateOrganizationMember(
  orgMemberData: OrganizationMemberUpdateData,
): Promise<Member> {
  const { member } = await fetchJson<{ member: Member }>(
    "/api/member",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgMemberData),
      cache: "no-store",
    },
    { fallbackErrorMessage: "Failed to update organization user" },
  );

  return member;
}

/* Organization member invitations */

export async function getOrganizationMemberInvitationDetails(
  orgId: string,
  userId: string,
  token: string,
): Promise<OrganizationMemberInviteDetails | null> {
  const { memberInvitation } = await fetchJson<{
    memberInvitation?: OrganizationMemberInviteDetails | null;
  }>(
    `/api/member/public?orgId=${orgId}&userId=${userId}&token=${token}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    },
    {
      fallbackErrorMessage:
        "Failed to get organization member invitation details",
    },
  );

  return memberInvitation || null;
}

export async function updateOrganizationMemberByInivitationDetails(
  orgMemberInviteData: OrganizationMemberInviteUpdateData,
): Promise<{ userId: string | null; organizationId: string }> {
  return fetchJson<{ userId: string | null; organizationId: string }>(
    "/api/member/public",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgMemberInviteData),
      cache: "no-store",
    },
    {
      fallbackErrorMessage: `Failed to ${
        orgMemberInviteData.isDeclined ? "decline" : "accept"
      } organization member invite`,
    },
  );
}
